
import React, { useEffect, useState } from 'react';
import {
    Button,
    FormControl,
    FormLabel,
    Input,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Textarea,
    Select,
    Box,
    Flex,
    useColorModeValue,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { getUserData } from 'utils';
import { createAPIEndPoint } from 'config/api/api';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { logoutUser } from 'utils';
import Loader from 'loaders/Loader';
import Card from 'components/card/Card';
import BackButton from 'components/BackButton';
import ModelSelect from 'components/model-select/ModelSelect';
const AIAgentForm = () => {
    const { assistantId } = useParams();
    const location = useLocation();
    const { type } = location.state ?? {};
    const navigate = useNavigate()
    const userData = getUserData();
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const clinic_id = userData?.clinic_id ?? 1;
    const [clinics, setClinics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingAssistant, setFetchingAssitant] = useState(false);
    const [initialData, setInitialData] = useState(null);



    console.log('🆔 Assistant ID:', assistantId);
    console.log('📄 Type:', type);

    const fetchAssistant = async () => {
        try {
            setFetchingAssitant(true)
            const response = await createAPIEndPoint(`assistants/`).fetchById(assistantId);
            setInitialData(response.data);
        } catch (err) {
            toast.error('Error fetching assistant data');
        } finally {
            setFetchingAssitant(false)
        }
    };

    useEffect(() => {
        if (assistantId && type === 'edit') {
            fetchAssistant();
        }
    }, [assistantId]);

    const fetchClinics = async () => {
        try {
            const response = await createAPIEndPoint('clinic').fetchAll(); // Still fetching assistants
            setClinics(response.data.clinics || []);
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Error fetching clinics');
            if (err?.response?.data?.error?.includes('Bearer token')) {
                logoutUser(navigate);
            }
        }
    };

    useEffect(() => {
        fetchClinics();
    }, []);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            clinic_id: initialData?.clinic_id?.toString() || '',
            service: initialData?.service || 'openai',
            type: initialData?.type || '',
            name: initialData?.name || '',
            instructions: initialData?.instructions || '',
            model: initialData?.model || 'gpt-4',
            temperature: parseFloat(initialData?.temperature) || 0.5,
            listen: initialData?.listen || '',
            prompt: initialData?.prompt || '',
            speak: initialData?.speak || '',
            vapi_payload: {
                name: initialData?.vapi_payload?.name || '',
                firstMessage: initialData?.vapi_payload?.firstMessage || '',
                endCallMessage: initialData?.vapi_payload?.endCallMessage || '',
                voicemailMessage: initialData?.vapi_payload?.voicemailMessage || '',
                firstMessageMode: initialData?.vapi_payload?.firstMessageMode || 'assistant-speaks-first',
                voice: {
                    provider: initialData?.vapi_payload?.voice?.provider || 'vapi',
                    voiceId: initialData?.vapi_payload?.voice?.voiceId || '',
                },
                model: {
                    provider: initialData?.vapi_payload?.model?.provider || 'openai',
                    model: initialData?.vapi_payload?.model?.model || '',
                    temperature: initialData?.vapi_payload?.model?.temperature || 0.5,
                    toolIds: initialData?.vapi_payload?.model?.toolIds || [],
                    messages: initialData?.vapi_payload?.model?.messages || [],
                    knowledgeBase: {
                        provider: initialData?.vapi_payload?.model?.knowledgeBase?.provider || '',
                        fileIds: initialData?.vapi_payload?.model?.knowledgeBase?.fileIds || [],
                    },
                },
                transcriber: {
                    provider: initialData?.vapi_payload?.transcriber?.provider || 'deepgram',
                    model: initialData?.vapi_payload?.transcriber?.model || '',
                    language: initialData?.vapi_payload?.transcriber?.language || 'en',
                    numerals: initialData?.vapi_payload?.transcriber?.numerals || false,
                    endpointing: initialData?.vapi_payload?.transcriber?.endpointing || 300,
                    confidenceThreshold: initialData?.vapi_payload?.transcriber?.confidenceThreshold || 0.4,
                },
                silenceTimeoutSeconds: initialData?.vapi_payload?.silenceTimeoutSeconds || 31,
                backgroundDenoisingEnabled: initialData?.vapi_payload?.backgroundDenoisingEnabled || false,
                hipaaEnabled: initialData?.vapi_payload?.hipaaEnabled || false,
                clientMessages: initialData?.vapi_payload?.clientMessages || [],
                serverMessages: initialData?.vapi_payload?.serverMessages || [],
                messagePlan: {
                    idleMessages: initialData?.vapi_payload?.messagePlan?.idleMessages || [],
                    idleMessageMaxSpokenCount: initialData?.vapi_payload?.messagePlan?.idleMessageMaxSpokenCount || 4,
                    idleTimeoutSeconds: initialData?.vapi_payload?.messagePlan?.idleTimeoutSeconds || 12,
                },
                analysisPlan: {
                    structuredDataPlan: {
                        messages: initialData?.vapi_payload?.analysisPlan?.structuredDataPlan?.messages || [],
                    },
                },
                startSpeakingPlan: {
                    waitSeconds: initialData?.vapi_payload?.startSpeakingPlan?.waitSeconds || 0.4,
                    transcriptionEndpointingPlan: {
                        onPunctuationSeconds: initialData?.vapi_payload?.startSpeakingPlan?.transcriptionEndpointingPlan?.onPunctuationSeconds || 0.1,
                        onNoPunctuationSeconds: initialData?.vapi_payload?.startSpeakingPlan?.transcriptionEndpointingPlan?.onNoPunctuationSeconds || 1.5,
                        onNumberSeconds: initialData?.vapi_payload?.startSpeakingPlan?.transcriptionEndpointingPlan?.onNumberSeconds || 0.5,
                    },
                    smartEndpointingEnabled: initialData?.vapi_payload?.startSpeakingPlan?.smartEndpointingEnabled || '',
                },
                server: {
                    url: initialData?.vapi_payload?.server?.url || '',
                    timeoutSeconds: initialData?.vapi_payload?.server?.timeoutSeconds || 20,
                },
            }

        },
        validationSchema: Yup.object({
            clinic_id: Yup.string()
                .required('Please select a clinic.'),
            service: Yup.string().required('Please select a service type.'),
            type: Yup.string().required('Please select a assistant type.'),
            name: Yup.string()
                .required('Assistant name is required.'),
            instructions: Yup.string()
                .required('Please provide assistant instructions.'),
            model: Yup.string()
                .required('Model selection is required.'),
            temperature: Yup.number()
                .transform((value, originalValue) =>
                    typeof originalValue === 'string' && originalValue.trim() === ''
                        ? undefined
                        : value
                )
                .typeError('Temperature must be a valid number.')
                .min(0, 'Temperature must be at least 0.')
                .max(1, 'Temperature must be at most 1.')
                .required('Temperature is required.'),

        }),

        onSubmit: async (values, { resetForm }) => {
            try {
                setLoading(true);

                // Destructure deepgram fields out conditionally
                let payload = { ...values };

                // Add clinic info
                const selectedClinic = clinics.find(
                    (c) => c.id.toString() === values.clinic_id.toString()
                );

                // Adjust payload for Deepgram
                if (values.service === 'vapi') {
                    payload = {
                        clinic_id: parseInt(values.clinic_id),
                        clinic_name: selectedClinic?.clinic_name || '',
                        vapi_payload: values.vapi_payload,
                    };
                } else {
                    // Handle openai or deepgram
                    const { listen, speak, prompt, ...rest } = values;
                    payload = {
                        ...rest,
                        clinic_id: parseInt(values.clinic_id),
                        clinic_name: selectedClinic?.clinic_name || '',
                    };

                    if (values.service === 'deepgram') {
                        payload.messages = prompt;
                    }
                }


                payload.clinic_id = parseInt(values.clinic_id);
                payload.clinic_name = selectedClinic?.clinic_name || '';

                if (type === 'edit') {
                    await createAPIEndPoint('assistants/').update(assistantId, payload);
                } else {
                    await createAPIEndPoint('CreateAssistant').create(payload);
                    toast.success('Assistant created');
                }

                resetForm();
                navigate('/admin/ai-agents');
            } catch (error) {
                console.error(error);
                toast.error(error.response?.data?.error || 'Something went wrong');
            } finally {
                setLoading(false);
            }
        }


        // onSubmit: async (values, { resetForm }) => {
        //     try {
        //         setLoading(true);
        //         if (type === 'edit') {
        //             const selectedClinic = clinics.find(
        //                 (c) => c.id.toString() === values.clinic_id.toString()
        //             );

        //             const payload = {
        //                 ...values,
        //                 clinic_id: parseInt(values.clinic_id),
        //                 clinic_name: selectedClinic?.clinic_name || '',
        //             };

        //             await createAPIEndPoint(`assistants/`).update(assistantId, payload);
        //         }
        //         else {
        //             const selectedClinic = clinics.find((c) => c.id.toString() === values.clinic_id.toString());
        //             const payload = {
        //                 ...values,
        //                 clinic_name: selectedClinic?.clinic_name || '',
        //                 clinic_id: selectedClinic?.id,
        //             };
        //             await createAPIEndPoint('CreateAssistant').create(payload);
        //             toast.success('Assistant created');
        //         }

        //         resetForm();
        //     } catch (error) {
        //         console.error(error);
        //         toast.error(error.response?.data?.error || 'Something went wrong');
        //     } finally {
        //         setLoading(false);
        //     }
        // },
    });

    return (
        <Box pt={{ base: '80px', md: '80px', xl: '80px' }}>
            <>
                <BackButton />
                <Card flexDirection="column" w="100%" px="0px" overflowX="auto">

                    <Flex
                        px={{ base: '12px', md: '25px' }}
                        mb="8px"
                        justifyContent="space-between"
                        align="center"
                        flexDirection={{ base: 'column', md: 'row' }}
                        gap={{ base: 2, md: 0 }}
                        w="100%"
                    >
                        <Text
                            color={textColor}
                            fontSize="24px"
                            fontWeight="700"
                            lineHeight="100%"
                            mb={{ base: '8px', md: '0px' }}
                            textAlign={{ base: 'center', md: 'left' }}
                        >
                            {type === 'edit' ? 'Edit Assistant' : 'Add Assistant'}

                        </Text>
                    </Flex>
                    {fetchingAssistant && <Loader isAbsolute={true} />}

                    <form onSubmit={formik.handleSubmit}>
                        <Box overflowY="auto" pb={2} pt={5} px={{ base: '12px', md: '25px' }}>
                            {/* Clinic Dropdown */}
                            <FormControl isInvalid={formik.touched.clinic_id && formik.errors.clinic_id} mb={3}>
                                <FormLabel>Clinic</FormLabel>
                                <Select
                                    name="clinic_id"
                                    value={formik.values.clinic_id}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    fontSize="sm"
                                    fontWeight="500"
                                    variant="auth"
                                    placeholder="Select Clinic"
                                >
                                    {clinics.map((c) => (
                                        <option key={c.id} value={c.id}>{c.clinic_name}</option>
                                    ))}
                                </Select>
                                {formik.touched.clinic_id && formik.errors.clinic_id && (
                                    <Text color="red.500" fontSize="sm">{formik.errors.clinic_id}</Text>
                                )}
                            </FormControl>

                            <FormControl isInvalid={formik.touched.service && formik.errors.service} mb={3}>
                                <FormLabel>Service Type</FormLabel>
                                <Select
                                    name="service"
                                    value={formik.values.service}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    fontSize="sm"
                                    fontWeight="500"
                                    variant="auth"
                                >
                                    <option value="openai">Open AI</option>
                                    <option value="deepgram">Deepgram</option>
                                    <option value="vapi">Vapi</option>
                                </Select>

                                {formik.touched.service && formik.errors.service && (
                                    <Text color="red.500" fontSize="sm">{formik.errors.service}</Text>
                                )}
                            </FormControl>

                            <FormControl isInvalid={formik.touched.type && formik.errors.type} mb={3}>
                                <FormLabel>Assistant Type</FormLabel>
                                <Select
                                    name="type"
                                    value={formik.values.type}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    fontSize="sm"
                                    fontWeight="500"
                                    variant="auth"
                                    placeholder="Select type"
                                >
                                    <option value="email">Email</option>
                                    <option value="chat">Chat</option>
                                    <option value="sms">SMS</option>
                                    <option value="text">Text</option>
                                    <option value="voice">Voice</option>
                                </Select>
                                {formik.touched.type && formik.errors.type && (
                                    <Text color="red.500" fontSize="sm">{formik.errors.type}</Text>
                                )}
                            </FormControl>


                            {/* {['name', 'instructions', 'model', 'temperature'].map((field, idx) => {
                                const isInvalid = formik.touched[field] && formik.errors[field];
                                const commonProps = {
                                    name: field,
                                    fontSize: "sm",
                                    fontWeight: "500",
                                    value: formik.values[field],
                                    onBlur: formik.handleBlur,
                                };

                                return (
                                    <FormControl key={idx} isInvalid={isInvalid} mb={3}>
                                        <FormLabel textTransform="capitalize">{field}</FormLabel>

                                        {field === 'instructions' ? (
                                            <Textarea
                                                {...commonProps}
                                                placeholder="Enter instructions"
                                                resize="vertical"
                                                minH="120px"
                                                onChange={formik.handleChange}
                                            />
                                        ) : field === 'temperature' ? (
                                            <Input
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="1"
                                                placeholder="Enter temperature"
                                                variant="auth"
                                                {...commonProps}
                                                onChange={(e) => {
                                                    const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                                                    formik.setFieldValue(field, value);
                                                }}
                                            />
                                        ) : (
                                            <Input
                                                placeholder={`Enter ${field}`}
                                                variant="auth"
                                                {...commonProps}
                                                onChange={formik.handleChange}
                                            />
                                        )}

                                        {isInvalid && (
                                            <Text color="red.500" fontSize="sm">
                                                {formik.errors[field]}
                                            </Text>
                                        )}
                                    </FormControl>
                                );
                            })} */}

                            {/* Conditional Fields Based on Service Type */}
                            {formik.values.service && (
                                <>
                                    <FormControl isInvalid={formik.touched.name && formik.errors.name} mb={3}>
                                        <FormLabel>Name</FormLabel>
                                        <Input
                                            name="name"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter name"
                                            fontSize="sm"
                                            fontWeight="500"
                                            variant="auth" />
                                        {formik.touched.name && formik.errors.name && (
                                            <Text color="red.500" fontSize="sm">{formik.errors.name}</Text>
                                        )}
                                    </FormControl>

                                    <ModelSelect formik={formik} serviceType={formik.values.service} />

                                    {formik.values.service === 'deepgram' && (
                                        <>
                                            {['listen', 'speak', 'prompt'].map((field) => (
                                                <FormControl key={field} mb={3}>
                                                    <FormLabel textTransform="capitalize">{field}</FormLabel>
                                                    {['listen', 'speak'].includes(field) ? (
                                                        <Input
                                                            name={field}
                                                            value={formik.values[field] || ''}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            placeholder={`Enter ${field}`}
                                                            fontSize="sm"
                                                            fontWeight="500"
                                                            variant="auth"
                                                        />
                                                    ) : (
                                                        <Textarea
                                                            name={field}
                                                            value={formik.values[field] || ''}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            placeholder={`Enter ${field}`}
                                                            resize="vertical"
                                                            minH="100px"
                                                            fontSize="sm"
                                                            fontWeight="500"
                                                            border="1px solid"
                                                            borderColor="gray.200"
                                                            borderRadius="8px"
                                                            px="14px"
                                                            py="10px"
                                                            _focus={{
                                                                borderColor: 'brand.500',
                                                                boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                                                            }}
                                                        />
                                                    )}
                                                </FormControl>
                                            ))}

                                        </>
                                    )}

                                    <FormControl isInvalid={formik.touched.instructions && formik.errors.instructions} mb={3}>
                                        <FormLabel>Instructions</FormLabel>
                                        <Textarea
                                            name="instructions"
                                            value={formik.values.instructions}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter instructions"
                                            resize="vertical"
                                            minH="120px"
                                            fontSize="sm"
                                            fontWeight="500"
                                            border="1px solid"
                                            borderColor="gray.200"
                                            borderRadius="8px"
                                            px="14px"
                                            py="10px"
                                            _focus={{
                                                borderColor: 'brand.500',
                                                boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                                            }} />
                                        {formik.touched.instructions && formik.errors.instructions && (
                                            <Text color="red.500" fontSize="sm">{formik.errors.instructions}</Text>
                                        )}
                                    </FormControl>


                                    {/* <FormControl isInvalid={formik.touched.model && formik.errors.model} mb={3}>
                                        <FormLabel>Model</FormLabel>
                                        <Input
                                            name="model"
                                            value={formik.values.model}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter model"
                                            fontSize="sm"
                                            fontWeight="500"
                                            variant="auth" />
                                        {formik.touched.model && formik.errors.model && (
                                            <Text color="red.500" fontSize="sm">{formik.errors.model}</Text>
                                        )}
                                    </FormControl> */}

                                    <FormControl isInvalid={formik.touched.temperature && formik.errors.temperature} mb={3}>
                                        <FormLabel>Temperature</FormLabel>
                                        <Input
                                            name="temperature"
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            max="1"
                                            value={formik.values.temperature}
                                            onChange={(e) =>
                                                formik.setFieldValue('temperature', e.target.value === '' ? '' : parseFloat(e.target.value))
                                            }
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter temperature"
                                            fontSize="sm"
                                            fontWeight="500"
                                            variant="auth" />
                                        {formik.touched.temperature && formik.errors.temperature && (
                                            <Text color="red.500" fontSize="sm">{formik.errors.temperature}</Text>
                                        )}
                                    </FormControl>
                                    {formik.values.service === 'vapi' && (
                                        <>
                                            <FormControl mb={3}>
                                                <FormLabel>First Message</FormLabel>
                                                <Textarea
                                                    name="vapi_payload.firstMessage"
                                                    value={formik.values.vapi_payload.firstMessage}
                                                    onChange={formik.handleChange}
                                                    placeholder="Welcome message"
                                                    minH="80px"
                                                    fontSize="sm"
                                                    fontWeight="500"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    borderRadius="8px"
                                                    px="14px"
                                                    py="10px"
                                                    _focus={{
                                                        borderColor: 'brand.500',
                                                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                                                    }}
                                                />
                                            </FormControl>

                                            <FormControl mb={3}>
                                                <FormLabel>End Call Message</FormLabel>
                                                <Textarea
                                                    name="vapi_payload.endCallMessage"
                                                    value={formik.values.vapi_payload.endCallMessage}
                                                    onChange={formik.handleChange}
                                                    placeholder="Goodbye message"
                                                    minH="80px"
                                                    fontSize="sm"
                                                    fontWeight="500"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    borderRadius="8px"
                                                    px="14px"
                                                    py="10px"
                                                    _focus={{
                                                        borderColor: 'brand.500',
                                                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                                                    }}
                                                />
                                            </FormControl>

                                            <FormControl mb={3}>
                                                <FormLabel>Voicemail Message</FormLabel>
                                                <Textarea
                                                    name="vapi_payload.voicemailMessage"
                                                    value={formik.values.vapi_payload.voicemailMessage}
                                                    onChange={formik.handleChange}
                                                    placeholder="Voicemail fallback"
                                                    minH="80px"
                                                    fontSize="sm"
                                                    fontWeight="500"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    borderRadius="8px"
                                                    px="14px"
                                                    py="10px"
                                                    _focus={{
                                                        borderColor: 'brand.500',
                                                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                                                    }}
                                                />
                                            </FormControl>

                                            <FormControl mb={3}>
                                                <FormLabel>Voice ID</FormLabel>
                                                <Input
                                                    name="vapi_payload.voice.voiceId"
                                                    value={formik.values.vapi_payload.voice.voiceId}
                                                    onChange={formik.handleChange}
                                                    placeholder="Elliot"
                                                    fontSize="sm"
                                                    fontWeight="500"
                                                    variant="auth"
                                                />
                                            </FormControl>

                                            <FormControl mb={3}>
                                                <FormLabel>Model Name</FormLabel>
                                                <Input
                                                    name="vapi_payload.model.model"
                                                    value={formik.values.vapi_payload.model.model}
                                                    onChange={formik.handleChange}
                                                    placeholder="gpt-4.1-mini"
                                                    fontSize="sm"
                                                    fontWeight="500"
                                                    variant="auth"
                                                />
                                            </FormControl>
                                        </>
                                    )}


                                </>
                            )}




                        </Box>
                        <Box pt={2} px={{ base: '12px', md: '25px' }}>
                            <Button
                                isLoading={loading}
                                type="submit"
                                colorScheme="brand"
                                // w="100%"
                                mt={4}
                            >
                                {type === 'edit' ? 'Update Assistant' : 'Add Assistant'}
                            </Button>
                        </Box>
                    </form>
                </Card>
            </>
        </Box>
    );
};



export default AIAgentForm